import React, { Component } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth, trackAction } from '../lib/firebase';
import LoginScreen from './dashboard/LoginScreen';
import ConversationsPage from './dashboard/ConversationsPage';
import StatsPage from './dashboard/StatsPage';
import OrdersPage from './dashboard/OrdersPage';
import MenuConfigPage from './dashboard/MenuConfigPage';
import AdminPanel from './dashboard/AdminPanel';
import ProductsPage from './dashboard/ProductsPage';
import ReportsPage from './dashboard/ReportsPage';
import '../styles/WhatsAppDashboard.css';

const WORKER_URL = 'https://rakeen-whatsapp-poc.thamer-21d.workers.dev';

const NAV_ITEMS = [
  { id: 'stats', icon: '📊', label: 'الرئيسية', roles: ['customer', 'admin'] },
  { id: 'conversations', icon: '💬', label: 'المحادثات', roles: ['customer', 'admin'] },
  { id: 'orders', icon: '📋', label: 'الطلبات', roles: ['customer', 'admin'] },
  { id: 'products', icon: '📦', label: 'المنتجات', roles: ['customer', 'admin'] },
  { id: 'menu', icon: '⚙️', label: 'القائمة', roles: ['customer', 'admin'] },
  { id: 'reports', icon: '📊', label: 'التقارير', roles: ['admin'] },
  { id: 'admin', icon: '📈', label: 'الاستخدام', roles: ['admin'] },
];

class WhatsAppDashboard extends Component {
  constructor(props) {
    super(props);
    const savedDark = localStorage.getItem('sd-dark') !== 'false';
    this.state = {
      user: null,
      idToken: null,
      role: null,
      loading: true,
      activePage: 'stats',
      darkMode: savedDark,
    };
  }

  componentDidMount() {
    document.title = 'سريع — لوحة التحكم | Saree Dashboard';

    onAuthStateChanged(auth, async (user) => {
      if (user) {
        const idToken = await user.getIdToken();
        await this.fetchRole(user, idToken);
      } else {
        this.setState({ user: null, idToken: null, role: null, loading: false });
      }
    });
  }

  trackEvent = async (type, data = {}) => {
    const { idToken } = this.state;
    if (!idToken) return;
    try {
      await fetch(`${WORKER_URL}/api/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${idToken}` },
        body: JSON.stringify({ type, data }),
      });
    } catch (err) { /* silent */ }
  };

  fetchRole = async (user, idToken) => {
    try {
      const res = await fetch(`${WORKER_URL}/api/auth/role`, {
        headers: { 'Authorization': `Bearer ${idToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        this.setState({ user, idToken, role: data.role, loading: false });
      } else {
        this.setState({ user, idToken, role: null, loading: false });
      }
    } catch (err) {
      console.error('Failed to fetch role:', err);
      this.setState({ user, idToken, role: null, loading: false });
    }
  };

  handleLogin = async (user, idToken) => {
    await this.fetchRole(user, idToken);
    setTimeout(() => this.trackEvent('login'), 500);
    trackAction('dashboard_login', { email: user.email });
  };

  handleLogout = async () => {
    trackAction('dashboard_logout');
    await signOut(auth);
    this.setState({ user: null, idToken: null, role: null });
  };

  setActivePage = (page) => {
    this.setState({ activePage: page });
    this.trackEvent('page_view', { page });
  };

  toggleDarkMode = () => {
    const next = !this.state.darkMode;
    localStorage.setItem('sd-dark', next);
    this.setState({ darkMode: next });
  };

  renderContent() {
    const { activePage, role, idToken } = this.state;

    switch (activePage) {
      case 'stats':
        return <StatsPage idToken={idToken} onNavigate={this.setActivePage} />;
      case 'conversations':
        return <ConversationsPage idToken={idToken} />;
      case 'orders':
        return <OrdersPage idToken={idToken} onNavigate={this.setActivePage} />;
      case 'products':
        return <ProductsPage idToken={idToken} />;
      case 'menu':
        return <MenuConfigPage idToken={idToken} role={role} />;
      case 'reports':
        return role === 'admin' ? <ReportsPage idToken={idToken} role={role} /> : null;
      case 'admin':
        return role === 'admin' ? <AdminPanel idToken={idToken} /> : null;
      default:
        return null;
    }
  }

  render() {
    const { user, role, loading, activePage, darkMode } = this.state;

    if (loading) {
      return (
        <div className="sd-loading">
          <div className="sd-loading-spinner" />
          <p>جاري التحميل...</p>
        </div>
      );
    }

    if (!user || !role) {
      return <LoginScreen onLogin={this.handleLogin} />;
    }

    const visibleNav = NAV_ITEMS.filter((item) => item.roles.includes(role));

    return (
      <div className={`sd-root ${darkMode ? 'sd-dark' : 'sd-light'}`}>
        {/* Sidebar */}
        <aside className="sd-sidebar">
          <div className="sd-sidebar-header">
            <img src="/images/rakeen-logo.png" alt="Rakeen" className="sd-sidebar-logo" />
            <span className="sd-sidebar-title">سريع</span>
          </div>

          <nav className="sd-sidebar-nav">
            {visibleNav.map((item) => (
              <button
                key={item.id}
                className={`sd-nav-item ${activePage === item.id ? 'active' : ''}`}
                onClick={() => this.setActivePage(item.id)}
              >
                <span className="sd-nav-icon">{item.icon}</span>
                <span className="sd-nav-label">{item.label}</span>
              </button>
            ))}
          </nav>

          <div className="sd-sidebar-footer">
            <div className="sd-user-info">
              <span className="sd-user-role">{role === 'admin' ? '🔧 مدير' : '👤 مستخدم'}</span>
              <span className="sd-user-email">{user.email}</span>
            </div>
            <button className="sd-logout-btn" onClick={this.handleLogout}>
              خروج
            </button>
          </div>
        </aside>

        {/* Mobile Bottom Tabs */}
        <nav className="sd-mobile-tabs">
          {visibleNav.map((item) => (
            <button
              key={item.id}
              className={`sd-tab-item ${activePage === item.id ? 'active' : ''}`}
              onClick={() => this.setActivePage(item.id)}
            >
              <span className="sd-tab-icon">{item.icon}</span>
              <span className="sd-tab-label">{item.label}</span>
            </button>
          ))}
        </nav>

        {/* Content */}
        <main className="sd-content">
          <header className="sd-header">
            <h1 className="sd-header-title">
              {visibleNav.find((n) => n.id === activePage)?.icon}{' '}
              {visibleNav.find((n) => n.id === activePage)?.label}
            </h1>
            <div className="sd-header-actions">
              <button className="sd-theme-toggle" onClick={this.toggleDarkMode} title={darkMode ? 'الوضع الفاتح' : 'الوضع الداكن'}>
                {darkMode ? '☀️' : '🌙'}
              </button>
              <span className="sd-header-user">{user.email}</span>
              <button className="sd-header-logout" onClick={this.handleLogout}>خروج</button>
            </div>
          </header>

          <div className="sd-page">
            {this.renderContent()}
          </div>
        </main>
      </div>
    );
  }
}

export default WhatsAppDashboard;
